import {
  getPosePhotoFn,
  getWorkoutPoseRoutineOptions,
  saveWorkoutPoseRoutineFn,
  uploadPosePhotoFn,
} from "@/actions/workout-pose/handler";
import {
  type PoseItem,
  type PosingRoutineData,
  standardPoses,
} from "@/api/db/schema/workout-poses";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Calendar,
  Camera,
  ChevronDown,
  ChevronUp,
  Maximize2,
  Sparkles,
  Trash2,
} from "lucide-react";
import { type ChangeEvent, useRef, useState } from "react";
import { toast } from "sonner";

export function PosePhoto({
  photoKey,
  className,
  alt = "Poz fotoğrafı",
}: {
  photoKey: string;
  className?: string;
  alt?: string;
}) {
  const { data, isError, refetch } = useQuery({
    queryKey: ["posePhoto", photoKey],
    queryFn: () => getPosePhotoFn({ data: { key: photoKey } }),
    staleTime: 1000 * 60 * 30, // 30 minutes
  });

  if (isError) {
    return (
      <div className="flex h-full w-full flex-col items-center justify-center bg-muted/40 p-2 text-center">
        <p className="text-[11px] text-destructive">Yüklenemedi</p>
        <button
          type="button"
          onClick={() => void refetch()}
          className="mt-1 text-[10px] text-primary underline"
        >
          Tekrar dene
        </button>
      </div>
    );
  }

  return data ? (
    <img
      src={data.url}
      alt={alt}
      className={cn("h-full w-full object-cover", className)}
      loading="lazy"
    />
  ) : (
    <div className="flex h-full w-full items-center justify-center bg-muted/30">
      <span className="animate-pulse text-[10px] text-muted-foreground">
        Yükleniyor...
      </span>
    </div>
  );
}

interface LocalPoseDraft {
  id: string;
  photoKey?: string;
  file?: File;
  previewUrl?: string;
  poseTag: string;
  notes?: string | null;
}

export function WorkoutPosingSection({
  programId,
  date,
  onDateChange,
}: {
  programId: string;
  date: string;
  onDateChange: (date: string) => void;
}) {
  const queryClient = useQueryClient();
  const [isOpen, setIsOpen] = useState(false);
  const [activePreviewUrl, setActivePreviewUrl] = useState<string | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);

  // Fetch current day's saved posing routine
  const { data: routineData } = useQuery(getWorkoutPoseRoutineOptions(date));

  const savedRoutine = (routineData?.data as PosingRoutineData | undefined) ?? {
    photos: [],
    notes: null,
    pumpScore: null,
  };

  const [draftPhotos, setDraftPhotos] = useState<LocalPoseDraft[]>([]);
  const [notes, setNotes] = useState<string>("");
  const [pumpScore, setPumpScore] = useState<number | null>(null);
  const [hasInitialized, setHasInitialized] = useState(false);

  // Sync draft state with loaded query data
  if (routineData !== undefined && !hasInitialized) {
    setDraftPhotos(
      (savedRoutine.photos ?? []).map((p) => ({
        id: p.id,
        photoKey: p.photoKey,
        poseTag: p.poseTag,
        notes: p.notes,
      })),
    );
    setNotes(savedRoutine.notes ?? "");
    setPumpScore(savedRoutine.pumpScore ?? null);
    setHasInitialized(true);
  }

  // Handle date change: reset initialization flag
  const handleDateSelect = (newDate: string) => {
    setHasInitialized(false);
    onDateChange(newDate);
  };

  const photoCount = draftPhotos.length;
  const hasSavedPhotos = (savedRoutine.photos?.length ?? 0) > 0;

  const mutation = useMutation({
    mutationFn: async () => {
      // 1. Upload any new files to S3
      const finalizedPhotos: PoseItem[] = [];

      for (const draft of draftPhotos) {
        let photoKey = draft.photoKey;

        if (draft.file) {
          const uploadRes = await uploadPosePhotoFn({
            data: {
              contentType: draft.file.type as
                | "image/jpeg"
                | "image/png"
                | "image/webp",
              size: draft.file.size,
            },
          });

          const upload = await fetch(uploadRes.url, {
            method: "PUT",
            headers: { "Content-Type": draft.file.type },
            body: draft.file,
          });

          if (!upload.ok) {
            throw new Error("Fotoğraf S3'e yüklenirken hata oluştu.");
          }

          photoKey = uploadRes.key;
        }

        if (photoKey) {
          finalizedPhotos.push({
            id: draft.id,
            photoKey,
            poseTag: draft.poseTag,
            notes: draft.notes || null,
          });
        }
      }

      // 2. Save posing routine record
      return await saveWorkoutPoseRoutineFn({
        data: {
          programId,
          date,
          data: {
            photos: finalizedPhotos,
            notes: notes.trim() || null,
            pumpScore: pumpScore ?? null,
          },
        },
      });
    },
    onSuccess: () => {
      toast.success("Poz rutini kaydedildi ve koçunuza iletildi.");
      void queryClient.invalidateQueries({
        queryKey: ["workoutPoseRoutine", date],
      });
      void queryClient.invalidateQueries({
        queryKey: ["clientWorkoutPoses"],
      });
    },
    onError: (err: any) => {
      toast.error(
        err.message || "Poz rutini kaydedilemedi. Lütfen tekrar deneyin.",
      );
    },
  });

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    const newDrafts: LocalPoseDraft[] = [];

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      if (
        !["image/jpeg", "image/png", "image/webp"].includes(file.type) ||
        file.size > 10 * 1024 * 1024
      ) {
        toast.error(
          `${file.name} geçerli değil (En fazla 10 MB JPEG/PNG/WebP).`,
        );
        continue;
      }

      const previewUrl = URL.createObjectURL(file);
      newDrafts.push({
        id: `draft-${Date.now()}-${Math.random().toString(36).substring(2, 7)}`,
        file,
        previewUrl,
        poseTag: "other",
        notes: null,
      });
    }

    setDraftPhotos((prev) => [...prev, ...newDrafts]);
    e.target.value = "";
  };

  const removePhoto = (id: string) => {
    setDraftPhotos((prev) => prev.filter((p) => p.id !== id));
  };

  const updatePhotoTag = (id: string, tag: string) => {
    setDraftPhotos((prev) =>
      prev.map((p) => (p.id === id ? { ...p, poseTag: tag } : p)),
    );
  };

  return (
    <>
      <div className="overflow-hidden rounded-2xl border border-border/70 bg-card/60 shadow-xs backdrop-blur-xs transition-all">
        {/* Header Bar */}
        <div
          role="button"
          tabIndex={0}
          onClick={() => setIsOpen((prev) => !prev)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              setIsOpen((prev) => !prev);
            }
          }}
          className="flex cursor-pointer select-none items-center justify-between p-3.5 transition-colors hover:bg-muted/20 sm:p-4"
        >
          <div className="flex min-w-0 items-center gap-2.5">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-amber-500/10 text-amber-500">
              <Sparkles className="h-4 w-4" />
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <span className="truncate font-semibold text-foreground text-sm">
                  Antrenman Sonrası Poz Rutini
                </span>
                {photoCount > 0 ? (
                  <Badge
                    variant="secondary"
                    className="h-5 bg-amber-500/15 px-1.5 font-bold text-[11px] text-amber-500"
                  >
                    {photoCount} Poz
                  </Badge>
                ) : (
                  <span className="hidden text-[11px] text-muted-foreground sm:inline">
                    (Vücut Geliştirme)
                  </span>
                )}
              </div>
              <p className="truncate text-[11px] text-muted-foreground">
                {photoCount > 0
                  ? `Kaydedilen: ${photoCount} poz${pumpScore ? ` · Pump: ${pumpScore}/5` : ""}`
                  : "Koçunun görmesi için antrenman sonrası pozlarını kaydet"}
              </p>
            </div>
          </div>

          <div className="flex shrink-0 items-center gap-1.5 sm:gap-2">
            {!isOpen && (
              <span className="hidden font-medium text-primary text-xs sm:inline">
                {hasSavedPhotos ? "Pozları Düzenle" : "+ Poz Ekle"}
              </span>
            )}
            <div className="flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-muted/50">
              {isOpen ? (
                <ChevronUp className="h-4 w-4" />
              ) : (
                <ChevronDown className="h-4 w-4" />
              )}
            </div>
          </div>
        </div>

        {/* Collapsible Content */}
        {isOpen && (
          <div className="space-y-4 border-border/50 border-t bg-muted/5 p-3.5 sm:p-4">
            {/* Top Toolbar: Date & Pump Rating */}
            <div className="flex flex-wrap items-center justify-between gap-3">
              {/* Pump Level Selector */}
              <div className="space-y-1">
                <span className="font-bold text-[10px] text-muted-foreground uppercase tracking-wider">
                  Pump & Kas Doluluğu
                </span>
                <div className="flex items-center gap-1">
                  {[1, 2, 3, 4, 5].map((score) => (
                    <button
                      key={score}
                      type="button"
                      onClick={() =>
                        setPumpScore((prev) => (prev === score ? null : score))
                      }
                      className={cn(
                        "flex h-7 min-w-[28px] items-center justify-center rounded-md px-1.5 font-bold text-xs transition-all",
                        pumpScore === score
                          ? "bg-amber-500 font-semibold text-black shadow-xs"
                          : "border border-border/60 bg-background text-muted-foreground hover:bg-muted/50",
                      )}
                    >
                      {score}
                      {score === 5 && "🔥"}
                    </button>
                  ))}
                  {pumpScore && (
                    <span className="ml-1 text-[11px] text-muted-foreground">
                      {pumpScore === 1
                        ? "Düşük"
                        : pumpScore === 2
                          ? "Normal"
                          : pumpScore === 3
                            ? "İyi"
                            : pumpScore === 4
                              ? "Çok İyi"
                              : "Maksimum!"}
                    </span>
                  )}
                </div>
              </div>

              {/* Compact Date Selector */}
              <div className="flex items-center gap-1.5 rounded-md border border-border/50 bg-background px-2.5 py-1 text-muted-foreground text-xs shadow-2xs">
                <Calendar className="h-3 w-3 shrink-0 text-muted-foreground" />
                <input
                  type="date"
                  value={date}
                  onChange={(e) => {
                    if (/^\d{4}-\d{2}-\d{2}$/.test(e.target.value)) {
                      handleDateSelect(e.target.value);
                    }
                  }}
                  className="w-[100px] cursor-pointer border-0 bg-transparent text-foreground text-xs focus:outline-none"
                />
              </div>
            </div>

            {/* Athlete Form Note for Coach */}
            <div className="space-y-1">
              <label
                htmlFor="posing-notes"
                className="font-bold text-[10px] text-muted-foreground uppercase tracking-wider"
              >
                Koça Form & Kondisyon Notu
              </label>
              <Textarea
                id="posing-notes"
                rows={2}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Örn: Sırt idmanı sonrası pump iyiydi, su atımı başladı, bel ince hissedildi..."
                className="resize-none bg-background text-xs"
              />
            </div>

            {/* Photo Cards Grid */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="font-bold text-[10px] text-muted-foreground uppercase tracking-wider">
                  Poz Fotoğrafları ({draftPhotos.length})
                </span>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  multiple
                  className="hidden"
                  onChange={handleFileChange}
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => fileInputRef.current?.click()}
                  className="h-7 gap-1.5 text-xs"
                >
                  <Camera className="h-3.5 w-3.5 text-primary" />
                  <span>Fotoğraf Ekle</span>
                </Button>
              </div>

              {draftPhotos.length === 0 ? (
                <div
                  role="button"
                  tabIndex={0}
                  onClick={() => fileInputRef.current?.click()}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      fileInputRef.current?.click();
                    }
                  }}
                  className="flex cursor-pointer flex-col items-center justify-center rounded-xl border border-border/70 border-dashed bg-muted/10 p-6 text-center transition-colors hover:bg-muted/20"
                >
                  <Camera className="mb-2 h-6 w-6 text-muted-foreground/60" />
                  <p className="font-medium text-xs">
                    Henüz poz fotoğrafı eklenmedi
                  </p>
                  <p className="mt-0.5 text-[11px] text-muted-foreground">
                    Ön/arka çift biceps, yan göğüs veya serbest pozlarınızı
                    ekleyin.
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3 md:grid-cols-4">
                  {draftPhotos.map((pose) => {
                    const previewSrc = pose.previewUrl;

                    return (
                      <div
                        key={pose.id}
                        className="group relative flex flex-col overflow-hidden rounded-xl border border-border/70 bg-card shadow-xs"
                      >
                        {/* Image Thumbnail Container (3:4 portrait aspect) */}
                        <div className="relative aspect-[3/4] w-full overflow-hidden bg-black/40">
                          {previewSrc ? (
                            <img
                              src={previewSrc}
                              alt="Yeni seçilen poz"
                              className="h-full w-full object-cover"
                            />
                          ) : pose.photoKey ? (
                            <PosePhoto photoKey={pose.photoKey} />
                          ) : null}

                          {/* Quick Actions Overlay */}
                          <div className="absolute inset-0 flex items-start justify-between bg-gradient-to-b from-black/60 via-transparent to-transparent p-1.5 opacity-90 transition-opacity group-hover:opacity-100">
                            {/* Fullscreen Preview Trigger */}
                            <button
                              type="button"
                              onClick={() => {
                                if (previewSrc) {
                                  setActivePreviewUrl(previewSrc);
                                } else if (pose.photoKey) {
                                  void getPosePhotoFn({
                                    data: { key: pose.photoKey },
                                  }).then((res) =>
                                    setActivePreviewUrl(res.url),
                                  );
                                }
                              }}
                              className="flex h-6 w-6 items-center justify-center rounded-md bg-black/60 text-white backdrop-blur-xs hover:bg-black"
                              title="Büyüt"
                            >
                              <Maximize2 className="h-3 w-3" />
                            </button>

                            {/* Delete Button */}
                            <button
                              type="button"
                              onClick={() => removePhoto(pose.id)}
                              className="flex h-6 w-6 items-center justify-center rounded-md bg-destructive/80 text-white backdrop-blur-xs hover:bg-destructive"
                              title="Sil"
                            >
                              <Trash2 className="h-3 w-3" />
                            </button>
                          </div>
                        </div>

                        {/* Pose Tag Selector */}
                        <div className="p-1.5">
                          <Select
                            value={pose.poseTag}
                            onValueChange={(val) =>
                              updatePhotoTag(pose.id, val)
                            }
                          >
                            <SelectTrigger className="h-7 border-border/50 text-[11px] focus:ring-0">
                              <SelectValue placeholder="Poz Türü" />
                            </SelectTrigger>
                            <SelectContent>
                              {standardPoses.map((p) => (
                                <SelectItem
                                  key={p.id}
                                  value={p.id}
                                  className="text-xs"
                                >
                                  {p.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Bottom Actions Bar */}
            <div className="flex items-center justify-between border-border/40 border-t pt-2">
              <span className="text-[11px] text-muted-foreground">
                Koçunuz bu pozları profilinizde inceleyebilir
              </span>
              <Button
                type="button"
                size="sm"
                onClick={() => mutation.mutate()}
                disabled={mutation.isPending}
                className="h-8 gap-1.5 rounded-lg bg-amber-500 px-4 font-semibold text-black hover:bg-amber-400"
              >
                {mutation.isPending ? "Kaydediliyor..." : "Pozları Kaydet"}
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Fullscreen Preview Lightbox Dialog */}
      <Dialog
        open={Boolean(activePreviewUrl)}
        onOpenChange={(open) => !open && setActivePreviewUrl(null)}
      >
        <DialogContent className="max-h-[95vh] w-[95vw] max-w-2xl overflow-hidden rounded-2xl border bg-black p-0">
          <DialogHeader className="p-3 text-left">
            <DialogTitle className="text-white text-xs">
              Poz Önizleme
            </DialogTitle>
          </DialogHeader>
          <div className="flex items-center justify-center p-2">
            {activePreviewUrl && (
              <img
                src={activePreviewUrl}
                alt="Büyük Poz Önizleme"
                className="max-h-[75vh] w-auto rounded-lg object-contain"
              />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
